import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { IEvaluationReview } from "../../model/evaluation/EvaluationReview";
import { IEvaluationRuleGroup } from "../../model/evaluation/EvaluationRuleGroup";
import GetEvaluationRuleGroupsInteractor from "../../interactor/evaluation/GetEvaluationRuleGroupsInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import GetEvaluationBusinessRulesInteractor from "../../interactor/evaluation/GetEvaluationBusinessRulesInteractor";
import { IEvaluationBusinessRule } from "../../model/evaluation/EvaluationBusinessRule";
import { IRegisteredApplicant } from "../../model/applicant/RegisteredApplicant";

export interface TEvaluationPresenter extends TLoadingAwarePresenter {
  evaluationReviews: IEvaluationReview[];
  evaluationRuleGroups: IEvaluationRuleGroup[];
  evaluationBusinessRules: IEvaluationBusinessRule[];
  applicant?: IRegisteredApplicant;
}

export interface IEvaluationPresenter
  extends TEvaluationPresenter,
    TPresentable {
  setEvaluationReviews(
    evaluationReviews: IEvaluationReview[],
    applicant?: IRegisteredApplicant
  ): void;
  onReviewExpand(expanded: boolean, record: IEvaluationReview): void;
  onRuleGroupExpand(expanded: boolean, record: IEvaluationRuleGroup): void;
}

const defaultState: TEvaluationPresenter = {
  evaluationReviews: [] as IEvaluationReview[],
  evaluationRuleGroups: [] as IEvaluationRuleGroup[],
  evaluationBusinessRules: [] as IEvaluationBusinessRule[],
  applicant: {} as IRegisteredApplicant
};

const EvaluationPresenter = withStore<
  IEvaluationPresenter,
  TEvaluationPresenter
>(({ application, store, loader, translate }): IEvaluationPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TEvaluationPresenter>();

  const setEvaluationReviews = (
    evaluationReviews: IEvaluationReview[],
    applicant?: IRegisteredApplicant
  ) => {
    _store.update({ evaluationReviews, applicant });
  };

  const onReviewExpand = async (
    expanded: boolean,
    record: IEvaluationReview
  ) => {
    if (expanded) {
      try {
        loader.start("evaluationRuleGroupsLoader");
        const evaluationRuleGroups = await _application.container
          .resolve<GetEvaluationRuleGroupsInteractor>("getEvaluationRuleGroups")
          .execute(record.reviewId);

        _store.update({ evaluationRuleGroups });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
      loader.stop("evaluationRuleGroupsLoader");
    }
  };

  const onRuleGroupExpand = async (
    expanded: boolean,
    record: IEvaluationRuleGroup
  ) => {
    if (expanded) {
      try {
        loader.start("evaluationBusinessRuleLoader");
        const evaluationBusinessRules = await _application.container
          .resolve<GetEvaluationBusinessRulesInteractor>(
            "getEvaluationBusinessRules"
          )
          .execute(record.businessRuleGroupEvaluationId);
        _store.update({ evaluationBusinessRules });
        loader.stop("evaluationBusinessRuleLoader");
      } catch (error) {
        loader.stop("evaluationBusinessRuleLoader");
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    }
  };

  const onRenderBreadcrumbLabel = (id: string) => {
    let state = _store.getState<TEvaluationPresenter>();
    return state.applicant?.name;
  };

  _store.update({
    breadCrumb: {
      ...(_store.getState() as any).breadCrumb,
      applicantId: onRenderBreadcrumbLabel
    }
  });

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    setEvaluationReviews,
    onReviewExpand,
    onRuleGroupExpand
  };
}, defaultState);

export default EvaluationPresenter;
